/**
 * AET AI Academy - AI Code Vibe Lexicon Professional
 * Type Definitions v2.0
 * 
 * @author AET AI Academy
 * @license MIT
 * @version 2.0.0
 */

// ============================================================================
// CORE TYPES
// ============================================================================

/**
 * Supported languages in the application
 */
export type Language = "cs" | "en";

/**
 * Skill level classification for terms
 */
export type SkillLevel = "Beginner" | "Intermediate" | "Advanced";

/**
 * Content pillars following MCG A-Z system
 * A = AI & Agents
 * B = Business & Productivity
 * H = Health & Longevity
 * M = Mindset & Resilience
 * Z = Zahrada (Garden) & Ecology
 */
export type ContentPillar = "A" | "B" | "H" | "M" | "Z";

/**
 * Types of minicourses available in MCG
 */
export type MiniCourseType = 
  | "quickstart"    // 5-10 min introduction
  | "deepdive"      // 20-30 min comprehensive
  | "usecase"       // 15-20 min practical application
  | "workshop"      // 30-45 min hands-on exercise
  | "masterclass";  // 60+ min expert level

/**
 * Categories for organizing lexicon terms
 */
export type TermCategory =
  | "Fundamentals"
  | "Prompting"
  | "Technology"
  | "Architecture"
  | "Ethics"
  | "Applications"
  | "Development"
  | "Data"
  | "Security"
  | "Business";

/**
 * Resource types for external links
 */
export type ResourceType = 
  | "article" 
  | "video" 
  | "course" 
  | "documentation" 
  | "tool"
  | "github"
  | "paper";

/**
 * View modes for displaying lexicon items
 */
export type ViewMode = "grid" | "list" | "compact";

/**
 * Sorting options for lexicon items
 */
export type SortOption = "alphabetical" | "level" | "category" | "recent" | "popularity";

/**
 * Theme options
 */
export type ThemeMode = "dark" | "light" | "auto";

// ============================================================================
// CONTENT STRUCTURES
// ============================================================================

/**
 * External resource reference
 */
export interface ResourceLink {
  /** Display title */
  title: string;
  /** Full URL */
  url: string;
  /** Resource type for icon/styling */
  type: ResourceType;
  /** Optional description */
  description?: string;
  /** Language of resource */
  language?: Language;
}

/**
 * Language-specific content block
 * Contains all translatable content for a term
 */
export interface LangBlock {
  /** The term itself */
  term: string;
  /** Brief one-line definition (max 100 chars) */
  shortDefinition: string;
  /** Full comprehensive definition */
  definition?: string;
  /** Practical example demonstrating usage */
  example?: string;
  /** Pro tip or best practice */
  tip?: string;
  /** Code example with syntax */
  codeExample?: string;
  /** List of practical use cases */
  useCases?: string[];
  /** Common mistakes to avoid */
  commonMistakes?: string[];
  /** Pronunciation guide (for technical terms) */
  pronunciation?: string;
  /** Alternative names/synonyms */
  synonyms?: string[];
  /** External resources */
  resources?: ResourceLink[];
}

/**
 * Minicourse link structure for MCG integration
 */
export interface MiniCourseLink {
  /** Unique course identifier */
  id: string;
  /** Course title */
  title: string;
  /** Course language */
  language: Language;
  /** Full URL to course */
  url: string;
  /** Course type */
  type: MiniCourseType;
  /** Duration in minutes */
  durationMinutes: number;
  /** Content pillar */
  pillar?: ContentPillar;
  /** Thumbnail image URL */
  thumbnail?: string;
  /** Prerequisite course IDs */
  prerequisites?: string[];
  /** Short description */
  description?: string;
  /** Difficulty rating 1-5 */
  difficulty?: number;
  /** Is course free? */
  isFree?: boolean;
}

/**
 * Main lexicon item structure
 * This is the core data type for each glossary entry
 */
export interface LexiconItem {
  // === Identity ===
  /** Unique URL-safe identifier (e.g., "few-shot-learning") */
  id: string;
  /** Semantic version of this entry */
  version?: string;
  
  // === Classification ===
  /** Skill level required to understand */
  level: SkillLevel;
  /** Primary category */
  category: string;
  /** Secondary category (optional) */
  subcategory?: string;
  /** Searchable tags */
  tags?: string[];
  
  // === MCG Integration ===
  /** A-Z letter for MCG collection */
  mcgLetter?: string;
  /** Content pillar assignment */
  pillar?: ContentPillar;
  /** Related minicourses */
  miniCourses?: MiniCourseLink[];
  
  // === Relations ===
  /** IDs of related terms */
  relatedIds?: string[];
  /** IDs of prerequisite terms */
  prerequisiteIds?: string[];
  /** IDs of advanced follow-up terms */
  advancedIds?: string[];
  /** Parent term ID (for nested terms) */
  parentId?: string;
  
  // === Bilingual Content ===
  /** Czech content */
  cs: LangBlock;
  /** English content */
  en: LangBlock;
  
  // === Metadata ===
  /** ISO date of creation */
  createdAt: string;
  /** ISO date of last update */
  updatedAt: string;
  /** Author name or ID */
  author?: string;
  /** Reviewer name or ID */
  reviewedBy?: string;
  /** View count for popularity */
  viewCount?: number;
  /** Is this a featured term? */
  isFeatured?: boolean;
  /** Is this term deprecated? */
  isDeprecated?: boolean;
  /** Deprecation notice if deprecated */
  deprecationNotice?: string;
}

// ============================================================================
// APPLICATION STATE
// ============================================================================

/**
 * Filter state for the application
 */
export interface FilterState {
  /** Current search query */
  searchQuery: string;
  /** Selected letter filter (null = all) */
  selectedLetter: string | null;
  /** Selected skill levels */
  selectedLevels: SkillLevel[];
  /** Selected categories */
  selectedCategories: TermCategory[];
  /** Selected content pillars */
  selectedPillars: ContentPillar[];
  /** Has minicourse filter */
  hasMiniCourse: boolean | null;
}

/**
 * UI state for the application
 */
export interface UIState {
  /** Current language */
  language: Language;
  /** Currently selected item for detail view */
  selectedItem: LexiconItem | null;
  /** Current view mode */
  viewMode: ViewMode;
  /** Current sort option */
  sortBy: SortOption;
  /** Current theme */
  theme: ThemeMode;
  /** Is modal open */
  isModalOpen: boolean;
  /** Is search focused */
  isSearchFocused: boolean;
  /** Is mobile menu open */
  isMobileMenuOpen: boolean;
}

/**
 * Complete application state
 */
export interface AppState extends FilterState, UIState {
  /** All lexicon items */
  items: LexiconItem[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Statistics about the lexicon
 */
export interface LexikonStatistics {
  /** Total number of terms */
  totalTerms: number;
  /** Count by level */
  byLevel: Record<SkillLevel, number>;
  /** Count by category */
  byCategory: Record<string, number>;
  /** Count by pillar */
  byPillar: Record<ContentPillar, number>;
  /** Count by letter */
  byLetter: Record<string, number>;
  /** Terms with minicourses */
  withMiniCourses: number;
  /** Total minicourses */
  totalMiniCourses: number;
  /** Languages available */
  languages: Language[];
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

/**
 * Props for the main App component
 */
export interface AppProps {
  /** Initial language override */
  initialLanguage?: Language;
  /** Initial theme override */
  initialTheme?: ThemeMode;
  /** Enable analytics */
  enableAnalytics?: boolean;
}

/**
 * Props for Header component
 */
export interface HeaderProps {
  language: Language;
  theme: ThemeMode;
  onLanguageChange: (lang: Language) => void;
  onThemeChange: (theme: ThemeMode) => void;
  statistics: LexikonStatistics;
}

/**
 * Props for FilterBar component
 */
export interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  categories: TermCategory[];
  statistics: LexikonStatistics;
  language: Language;
}

/**
 * Props for LetterNavigation component
 */
export interface LetterNavigationProps {
  letters: string[];
  activeLetter: string | null;
  letterCounts: Record<string, number>;
  onSelect: (letter: string | null) => void;
  language: Language;
}

/**
 * Props for LexikonCard component
 */
export interface LexikonCardProps {
  item: LexiconItem;
  language: Language;
  onClick: (item: LexiconItem) => void;
  isCompact?: boolean;
  searchQuery?: string;
}

/**
 * Props for LexikonGrid component
 */
export interface LexikonGridProps {
  items: LexiconItem[];
  language: Language;
  viewMode: ViewMode;
  onItemClick: (item: LexiconItem) => void;
  searchQuery?: string;
  emptyMessage?: string;
}

/**
 * Props for DetailModal component
 */
export interface DetailModalProps {
  item: LexiconItem;
  language: Language;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onShare: (item: LexiconItem) => void;
}

/**
 * Props for MiniCourseCard component
 */
export interface MiniCourseCardProps {
  course: MiniCourseLink;
  language: Language;
}

/**
 * Props for StatsBar component
 */
export interface StatsBarProps {
  statistics: LexikonStatistics;
  language: Language;
}

/**
 * Props for Footer component
 */
export interface FooterProps {
  language: Language;
}

// ============================================================================
// HOOK RETURN TYPES
// ============================================================================

/**
 * Return type for useFilter hook
 */
export interface UseFilterReturn {
  filteredItems: LexiconItem[];
  filters: FilterState;
  setFilters: (updates: Partial<FilterState>) => void;
  resetFilters: () => void;
  activeFilterCount: number;
}

/**
 * Return type for useDeepLink hook
 */
export interface UseDeepLinkReturn {
  currentHash: string | null;
  setHash: (id: string | null) => void;
  getItemFromHash: () => LexiconItem | null;
}

/**
 * Return type for useLocalStorage hook
 */
export interface UseLocalStorageReturn<T> {
  value: T;
  setValue: (value: T) => void;
  removeValue: () => void;
}

/**
 * Return type for useKeyboard hook
 */
export interface UseKeyboardReturn {
  registerShortcut: (key: string, callback: () => void, modifiers?: string[]) => void;
  unregisterShortcut: (key: string) => void;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Translation keys for UI strings
 */
export interface UITranslations {
  search: {
    placeholder: string;
    noResults: string;
    resultsCount: string;
  };
  filters: {
    all: string;
    level: string;
    category: string;
    pillar: string;
    clear: string;
    activeFilters: string;
  };
  levels: {
    Beginner: string;
    Intermediate: string;
    Advanced: string;
  };
  pillars: {
    A: string;
    B: string;
    H: string;
    M: string;
    Z: string;
  };
  modal: {
    definition: string;
    example: string;
    tip: string;
    code: string;
    related: string;
    minicourses: string;
    share: string;
    copy: string;
    close: string;
  };
  stats: {
    total: string;
    terms: string;
    courses: string;
  };
  common: {
    loading: string;
    error: string;
    retry: string;
  };
}

/**
 * Pillar configuration with metadata
 */
export interface PillarConfig {
  id: ContentPillar;
  name: {
    cs: string;
    en: string;
  };
  description: {
    cs: string;
    en: string;
  };
  color: string;
  icon: string;
}

/**
 * Level configuration with metadata
 */
export interface LevelConfig {
  id: SkillLevel;
  name: {
    cs: string;
    en: string;
  };
  color: string;
  bgColor: string;
  icon: string;
}

// ============================================================================
// EVENT TYPES
// ============================================================================

/**
 * Analytics event structure
 */
export interface AnalyticsEvent {
  type: "view" | "search" | "filter" | "click" | "share" | "error";
  payload: Record<string, unknown>;
  timestamp: string;
}

/**
 * Error boundary fallback props
 */
export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * All available letters for navigation
 */
export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * Default filter state
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  searchQuery: "",
  selectedLetter: null,
  selectedLevels: [],
  selectedCategories: [],
  selectedPillars: [],
  hasMiniCourse: null,
};

/**
 * Default UI state
 */
export const DEFAULT_UI_STATE: UIState = {
  language: "cs",
  selectedItem: null,
  viewMode: "grid",
  sortBy: "alphabetical",
  theme: "dark",
  isModalOpen: false,
  isSearchFocused: false,
  isMobileMenuOpen: false,
};

/**
 * Pillar configurations
 */
export const PILLAR_CONFIG: Record<ContentPillar, PillarConfig> = {
  A: {
    id: "A",
    name: { cs: "AI & Agenti", en: "AI & Agents" },
    description: { cs: "Umělá inteligence a autonomní systémy", en: "Artificial Intelligence and autonomous systems" },
    color: "#3b82f6",
    icon: "🤖",
  },
  B: {
    id: "B",
    name: { cs: "Byznys & Produktivita", en: "Business & Productivity" },
    description: { cs: "Podnikání a efektivita práce", en: "Business and work efficiency" },
    color: "#f59e0b",
    icon: "💼",
  },
  H: {
    id: "H",
    name: { cs: "Zdraví & Longevity", en: "Health & Longevity" },
    description: { cs: "Zdravý životní styl a dlouhověkost", en: "Healthy lifestyle and longevity" },
    color: "#10b981",
    icon: "💚",
  },
  M: {
    id: "M",
    name: { cs: "Mindset & Resilience", en: "Mindset & Resilience" },
    description: { cs: "Osobní rozvoj a mentální odolnost", en: "Personal development and mental resilience" },
    color: "#8b5cf6",
    icon: "🧠",
  },
  Z: {
    id: "Z",
    name: { cs: "Zahrada & Ekologie", en: "Garden & Ecology" },
    description: { cs: "Permakultura a udržitelnost", en: "Permaculture and sustainability" },
    color: "#84cc16",
    icon: "🌱",
  },
};

/**
 * Level configurations
 */
export const LEVEL_CONFIG: Record<SkillLevel, LevelConfig> = {
  Beginner: {
    id: "Beginner",
    name: { cs: "Začátečník", en: "Beginner" },
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.15)",
    icon: "🌱",
  },
  Intermediate: {
    id: "Intermediate",
    name: { cs: "Pokročilý", en: "Intermediate" },
    color: "#eab308",
    bgColor: "rgba(234, 179, 8, 0.15)",
    icon: "📈",
  },
  Advanced: {
    id: "Advanced",
    name: { cs: "Expert", en: "Advanced" },
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.15)",
    icon: "🚀",
  },
};

/**
 * Course type configurations
 */
export const COURSE_TYPE_CONFIG: Record<MiniCourseType, { name: { cs: string; en: string }; icon: string }> = {
  quickstart: { name: { cs: "Rychlý start", en: "Quickstart" }, icon: "⚡" },
  deepdive: { name: { cs: "Hloubkový kurz", en: "Deep Dive" }, icon: "🔬" },
  usecase: { name: { cs: "Případová studie", en: "Use Case" }, icon: "💡" },
  workshop: { name: { cs: "Workshop", en: "Workshop" }, icon: "🛠️" },
  masterclass: { name: { cs: "Masterclass", en: "Masterclass" }, icon: "🎓" },
};

export default {
  ALPHABET,
  DEFAULT_FILTER_STATE,
  DEFAULT_UI_STATE,
  PILLAR_CONFIG,
  LEVEL_CONFIG,
  COURSE_TYPE_CONFIG,
};
